/**
 * ARTWORK STUDIO — THE PAINT-BY-NUMBERS STEPS (pure; no React).
 *
 * Master Spec Section 2: every step is TWO things — a selector/input, and an
 * "add to the AI script" box whose text is injected into the render prompt for
 * that step. Section 4 fixes the order for the Championship ring, which is the
 * reference flow every other style is a subset of.
 *
 * `scriptKey` is the key the box posts under in `scripts{}`. The backend's
 * Layer 3 (`promptAssembler.js#layer3`) emits the config's known steps first
 * and then ANY other key the payload carries, so a box the shipped config does
 * not name is still injected — never swallowed. That is what lets the Ring
 * Scale step (a real selector, not one of the config's 12 script steps) carry a
 * script box honestly.
 *
 * `mergeSteps(config)` appends any script step the backend declares that this
 * list has not heard of, so a 13th backend step gets a box with no frontend
 * change.
 */

/** kind: which control the step draws. */
export const KIND = {
  LOGO: 'logo',           // HubSpot-owned logo picker OR manual attach/upload
  CANVA: 'canva',         // folder-scoped Canva picker (+ attach/upload fallback)
  CHOICE: 'choice',       // a fixed set of options
  TEXT: 'text',           // keyed text (exact type — typed, never picked)
  TITLE: 'title',         // title + suggest-don't-autofill variable attributes
  STONES: 'stones',       // shapes + setting + package
  METAL: 'metal',         // tone + Vibrantium ID dropper + finish
  SCALE: 'scale',
  COMPLEXITY: 'complexity',
  BACKGROUND: 'background',
}

/**
 * The Championship ring flow (Section 4), in order. `next` is the one line the
 * step shows before the operator acts — the paint-by-numbers property: you are
 * never asked to choose between three possible moves.
 */
export const CHAMPIONSHIP_STEPS = [
  {
    key: 'logoC', n: 1, kind: KIND.LOGO, title: 'Bezel Logo (C)', required: true,
    next: 'Pick the client crest from the owned HubSpot marks, or attach one. Everything downstream references it as C.',
    why: 'The prompt says "replace the placeholder logo with C". Without C the engine has nothing to swap in, and the backend refuses the build.',
  },
  {
    key: 'bezel', n: 2, kind: KIND.CANVA, folderKey: 'bezel',
    title: 'Bezel & Top Design',
    next: 'Open Bezel & Top Designs and pick the face. Its logo is a placeholder — C replaces it.',
    why: 'The bezel sets the ring geometry; the build is assembled top-down from it. The action-bezel option lives here — it is the innovation lever.',
  },
  {
    key: 'girdle', n: 3, kind: KIND.CHOICE, title: 'Girdle',
    next: 'Choose how many rows of channel-set team-color princess stones sit below the bezel.',
    why: 'Girdle rows are the fastest read of how substantial the piece is.',
  },
  {
    key: 'title', n: 4, kind: KIND.TITLE, title: 'Title + Variable Attributes', required: true,
    next: 'Write the exact title. Then include only the attributes you want — suggestions never write themselves.',
    why: 'The title is keyed type, rendered verbatim. You write it so the ring focuses on what the client cares about — "CWS" over the conference title.',
  },
  {
    key: 'shank1', n: 5, kind: KIND.CANVA, folderKey: 'shanks', title: 'Side Shank 1',
    next: 'Pick the left shank design, then key the exact text for panel A (name / number / award).',
    why: 'Panel A. The script box drives the extras — the record at the base, "HC" under a coach name.',
  },
  {
    key: 'shank2', n: 6, kind: KIND.CANVA, folderKey: 'shanks', title: 'Side Shank 2',
    next: 'Pick the right shank design, then key the exact text for panel B (year / record / emblem).',
    why: 'Panel B. Appendix A places the shank extras on this side.',
  },
  {
    key: 'palm', n: 7, kind: KIND.TEXT, title: 'Palm Side (interior text)',
    next: 'Key the inside-band story — the playoff ladder, the motto. Leave blank for clean metal.',
    why: 'Rendered as exact incised type. Blank is a real answer: the band renders unengraved rather than invented.',
  },
  {
    key: 'core', n: 8, kind: KIND.TEXT, title: 'Core / Interior Engraving',
    next: 'Describe the interior engraving and key the signature line.',
    why: 'The elaborate interior treatment plus the signature — both rendered verbatim.',
  },
  {
    key: 'stones', n: 9, kind: KIND.STONES, title: 'Feature Gemstones',
    next: 'Pick the shapes, the setting, and the package. Stone color follows the team colors.',
    why: 'Setting and package are what separate a $500 ring from a thin one — and they tie straight to pricing.',
  },
  {
    key: 'metal', n: 10, kind: KIND.METAL, title: 'Metal',
    next: 'Pick the metal and the finish. On a Vibrantium composition, the dropper pins an exact Vibrantium ID.',
    why: 'Metal plus finish is the whole material read. The Vibrantium ID pins an exact custom color instead of a described one — it applies to the Vibrantium compositions, not to a solid karat gold.',
  },
  {
    key: 'scale', n: 11, kind: KIND.SCALE, title: 'Ring Scale',
    next: 'Pick the physical size, Small through 6XL.',
    why: 'Scale is hard to read in a floating studio render, so the engine exaggerates shank mass and bezel wall as size climbs and adds an on-hand cue at the larger sizes.',
    passthrough: true, // not one of the config's 12 script steps — Layer 3 passthrough
  },
  {
    key: 'complexity', n: 12, kind: KIND.COMPLEXITY, title: 'Complexity',
    next: 'Set the level, 1 to 5.',
    why: '1 is clean and restrained; 5 is fully iced, Super-Bowl-grade. It scales stone density, halo richness and sculptural relief.',
  },
  {
    key: 'background', n: 13, kind: KIND.BACKGROUND, folderKey: 'backgrounds',
    title: 'Background / Presentation',
    next: 'Pick the background, then confirm the presentation line and who it is presented to.',
    why: 'This is where the Locked Layer is enforced on the presentation: the SR logo, the audience, and the "School – Sport – Championship – Year" line.',
  },
]

/**
 * The step list this build actually renders. Appends any script step the
 * backend config declares that the local flow has not heard of, so a new
 * backend step gets its own box (and its own number) with no frontend change.
 */
export function mergeSteps(config, base = CHAMPIONSHIP_STEPS, styleId = '') {
  const known = new Set(base.map((s) => s.key))
  /* ⚠️ THE ENGINE HAS NO PER-STYLE STEP CONCEPT YET, AND THIS IS THE HOOK.
   *
   * `/config.scriptSteps` is ONE global list (`renderService.js#artworkConfig`
   * returns `SCRIPT_STEPS` flat), so today every style would get the
   * Championship ring's twelve script slots. The per-style sets therefore live
   * in `productModel.js#STYLES.drops`, which is a frontend reading of the
   * object and is flagged as provisional on screen for exactly that reason.
   *
   * The authority is the beast. The moment `/config.styles[].scriptSteps`
   * ships — a named item in docs/BACKEND-HANDOVER.md — it wins here with no
   * other change anywhere. Reading it now rather than later is what makes that
   * true; a hook added on the day it is needed is a hook that gets forgotten. */
  const declared = styleId
    ? (config?.styles || []).find((s) => String(s.style || s.id) === String(styleId))
    : null
  const source = (declared && Array.isArray(declared.scriptSteps) && declared.scriptSteps.length)
    ? declared.scriptSteps
    : (config?.scriptSteps || [])
  const extra = source
    .filter((s) => s && s.key && !known.has(s.key))
    .map((s, i) => ({
      key: s.key,
      n: base.length + i + 1,
      kind: KIND.TEXT,
      title: s.label || s.key,
      next: 'Describe this step for the render script.',
      why: 'This step came from the engine config — the backend added it after this flow was written.',
      fromConfig: true,
    }))
  return extra.length ? [...base, ...extra] : base
}
